class TopazTabbedSlider extends HTMLElement {
  #abortController;
  #motionQuery;
  #observer;
  #signature = '';

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('slideshow:select', this.#handleSlideSelect, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('click', this.#handleTabClick, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#handleMotionPreference, { signal });

    this.#observer = new MutationObserver(this.#refresh);
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-tab-title'],
      childList: true,
      subtree: true,
    });

    customElements.whenDefined('slideshow-component').then(() => {
      if (!this.isConnected) return;
      this.#refresh();
      this.#handleMotionPreference();
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  get slideshow() {
    return this.querySelector(':scope > slideshow-component');
  }

  get tabs() {
    return [...this.querySelectorAll('.topaz-tabbed-slider__tab[role="tab"]')];
  }

  get panels() {
    return [...this.querySelectorAll('slideshow-slide[data-tab-index][data-tab-title]')];
  }

  #refresh = () => {
    const tabs = this.tabs;
    const panelsByTabIndex = new Map(this.panels.map((panel) => [panel.dataset.tabIndex, panel]));
    const signature = tabs
      .map((tab) => `${tab.id}:${panelsByTabIndex.get(tab.dataset.tabIndex)?.dataset.tabTitle ?? ''}`)
      .join('|');
    if (signature === this.#signature) return;

    this.#signature = signature;

    tabs.forEach((tab) => {
      const title = panelsByTabIndex.get(tab.dataset.tabIndex)?.dataset.tabTitle?.trim();
      tab.textContent = title || tab.dataset.fallbackTitle || '';
    });

    this.#sync(this.slideshow?.current ?? 0, false);
  };

  #handleSlideSelect = (event) => {
    if (event.target !== this.slideshow) return;

    this.#sync(event.detail.index, true);
    if (event.detail.userInitiated) this.slideshow?.pause();
  };

  #handleTabClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.topaz-tabbed-slider__tab[role="tab"]')) return;
    this.slideshow?.pause();
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('.topaz-tabbed-slider__tab[role="tab"]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const tabs = this.tabs;
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex < 0) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    let nextIndex;

    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (event.key === 'ArrowRight') nextIndex = currentIndex + (isRtl ? -1 : 1);
    if (event.key === 'ArrowLeft') nextIndex = currentIndex + (isRtl ? 1 : -1);
    if (nextIndex === undefined) return;

    event.preventDefault();
    nextIndex = (nextIndex + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
    this.slideshow?.pause();
    this.slideshow?.select(nextIndex, event);
  };

  #handleFocusIn = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.topaz-tabbed-slider__playback')) return;
    this.slideshow?.suspend();
  };

  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.slideshow?.resume();
  };

  #handleMotionPreference = () => {
    const reduceMotion = Boolean(this.#motionQuery?.matches);
    this.toggleAttribute('data-reduced-motion', reduceMotion);
    if (reduceMotion) this.slideshow?.pause();
  };

  #sync(index, centerTab) {
    const tabs = this.tabs;
    const panels = this.panels;
    const selectedTabIndex = tabs[index]?.dataset.tabIndex;

    tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });

    panels.forEach((panel) => {
      const selected = panel.dataset.tabIndex === selectedTabIndex;
      panel.toggleAttribute('inert', !selected);
      panel.setAttribute('aria-hidden', String(!selected));
    });

    if (centerTab) {
      const behavior = this.#motionQuery?.matches ? 'auto' : 'smooth';
      tabs[index]?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    }
  }
}

if (!customElements.get('topaz-tabbed-slider')) {
  customElements.define('topaz-tabbed-slider', TopazTabbedSlider);
}
